/**
 * Analytics backend — own host and `app.statsToken`, so not interchangeable with
 * ./operational.ts. Known-broken filter paths are ported as-is; see PROGRESS.md.
 */

import axiosLib from 'axios'
import constants from '../../constants'
import axiosInstance from '../axiosInstance'
import { isAuthenticated, getAuthHeader } from '../../utils/authSession'
import { toServiceError } from './errors'
import { AnalyticsEnvelope, MetricRangeParams, MetricsApp, MetricsServiceError } from './types'

/**
 * Fetches a single metric for a custom range, for one chart's period dropdown.
 *
 * @param metricHost Resolved analytics host — `helpers.getAppMetricHost(appSettings)`.
 */
export async function getByType(
  app: MetricsApp,
  metricHost: string,
  type: string,
  params: MetricRangeParams,
): Promise<AnalyticsEnvelope | MetricsServiceError> {
  let URL = constants.isLiveEnv || constants.isStagingEnv ? 'https://' : 'http://'

  if (type === 'dau' || type === 'mau' || type === 'hau')
    URL = `${URL}${metricHost}/v2/${type}/range/${params.startRange}/${params.endRange}`
  else if (type === 'ccu')
    URL = `${URL}${metricHost}/v1/${type}/range/${params.startRange}/${params.endRange}`
  else if (type === 'pcu')
    URL = `${URL}${metricHost}/v1/ccupeaks/?beginningTimestamp=${params.startRange}&endingTimestamp=${params.endRange}`
  else if (type === 'voice' || type === 'video')
    URL = `${URL}${metricHost}/v1/calls/statistics?beginningTimestamp=${params.startRange}&endingTimestamp=${params.endRange}&interval=day`
  // NOTE: no branch for 'mcu' / 'dcu' — see the file header. Intentionally kept.

  return axiosInstance
    .get(`${URL}`, {
      timeout: constants.timeout_in_ms.metrics,
      headers: {
        appId: app.id,
        Authorization: `Bearer ${app.statsToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
    .then(res => res.data)
    .catch(toServiceError)
}

/**
 * Insights data for the Analytics page. Uses bare axios, not the shared instance —
 * the caller passes the whole URL. Returns `false` with no bearer token.
 */
export async function getInsightsData(URL: string): Promise<unknown> {
  try {
    if (isAuthenticated()) {
      const res = await axiosLib.get(URL, { headers: getAuthHeader() })
      return res.data
    }
  } catch (error) {
    const err = error as { response?: { data?: unknown } }
    if (err.response !== undefined && err.response.data !== undefined) return err.response.data
    return error
  }
  return false
}

export default function init(): void {}
